import { useSelector, useDispatch } from "react-redux";
import {
  setUser,
  initialState as initialUserState,
} from "store/auth/userSlice";
import { apiSignUp } from "services/AuthService";
import {
  onSignInSuccess,
  onSignOutSuccess,
  setToken,
} from "store/auth/sessionSlice";
import appConfig from "configs/app.config";
import { REDIRECT_URL_KEY } from "constants/app.constant";
import { useNavigate } from "react-router-dom";
import useQuery from "./useQuery";
import PopNotification from "components/shared/PopNotification";
import Api from "api/auth/UserApi";

interface SignInValues {
  email: string;
  password: string;
}

interface SignUpValues {
  email: string;
  password: string;
  [key: string]: any;
}

interface AuthResponse {
  status: string;
  message: string;
}

function useAuth() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const query = useQuery();

  const { token, signedIn } = useSelector((state: any) => state.auth.session);
  const userInfo = useSelector((state: any) => state.auth.user);

  const signIn = async (values: SignInValues): Promise<AuthResponse> => {
    try {
      const resp: any = await Api.login(values);
      if (resp.data) {
        const { token, user } = resp.data.data;
        if (token) {
          dispatch(onSignInSuccess(token));
        }
        if (user) {
          dispatch(setUser(user));
        }
        const redirectUrl = query.get(REDIRECT_URL_KEY);
        navigate(redirectUrl ?? appConfig.authenticatedEntryPath);
        PopNotification("success", resp.data.status, "Login Berhasil.");
        return {
          status: resp.data.status,
          message: resp.data.message,
        };
      }
      return {
        status: "failed",
        message: "Unknown error",
      };
    } catch (errors: any) {
      return {
        status: errors?.response?.data?.status ?? "failed",
        message: errors?.response?.data?.message || errors.toString(),
      };
    }
  };

  const signUp = async (values: SignUpValues): Promise<AuthResponse> => {
    try {
      const resp = await apiSignUp(values);
      if (resp.data) {
        const { token } = resp.data;
        if (token) {
          dispatch(onSignInSuccess(token));
        }

        dispatch(
          setUser(
            resp.data.user ?? {
              avatar: "",
              userName: "Anonymous",
              authority: ["USER"],
              email: "",
            }
          )
        );

        const redirectUrl = query.get(REDIRECT_URL_KEY);
        navigate(redirectUrl ?? appConfig.authenticatedEntryPath);

        return {
          status: "success",
          message: "",
        };
      }
      return {
        status: "failed",
        message: "Unknown error",
      };
    } catch (errors: any) {
      return {
        status: "failed",
        message: errors?.response?.data?.message || errors.toString(),
      };
    }
  };

  const handleSignOut = () => {
    dispatch(onSignOutSuccess());
    dispatch(setUser(initialUserState));
    dispatch(setToken(null));
    navigate(appConfig.unAuthenticatedEntryPath);
  };

  const signOut = async () => {
    // await apiSignOut()
    handleSignOut();
  };

  const isAuthority = (val: string | string[]): boolean => {
    if (!userInfo?.authority) return false;
    if (Array.isArray(val)) {
      return val.some((role) => userInfo.authority.includes(role));
    }
    return userInfo.authority.includes(val);
  };

  const initialName: string =
    userInfo?.name
      ?.split(" ")
      .map((n: string) => n[0])
      .join("")
      .substring(0, 2)
      .toUpperCase() ?? "";

  return {
    authenticated: !!token && signedIn,
    signIn,
    signUp,
    signOut,
    isAuthority,
    userInfo,
    authorityList: userInfo?.authority ?? [],
    initialName,
  };
}

export default useAuth;
